import React, { useRef } from 'react';
import Icon from 'components/Utils/NewIcon';
import ChatMessage from './ChatMessage';
import ProgressMessage from 'components/Utils/ProgressMessage';
import ContentEditable from 'components/Utils/ContentEditable';
import DefaultButton from 'components/Utils/NewDefaultButton';
import SendSMSIcon from 'components/Misc/SendSMSIcon';
import careyIcon from 'assets/img/icons/avatar-ai.svg';
import { CAREY_DISPLAY_NAME } from 'constants/carey';

import './style.scss';

const ChatWindowView = (props) => (
  <div className="chat-window" key={'chat-window' - props.patient?.id}>
    <TopBar {...props} />
    <div className="messages scroll-list">
      <>
        {/* Optional banner that sits at the top of the scroll list and scrolls away with the
            conversation — used by Carey rooms so the AI-scope notice doesn't permanently
            consume vertical space (especially on mobile). */}
        {props.scrollHeader}
        {/* TEST MODE — remove with the CareyArtifactPreview wiring. Rendered inside the scroll
            list so a tall preview can be scrolled alongside real chat content. */}
        {props.previewContent}
        {props.groupedMessages.length === 0 && props.welcomeMessage}
        <ProgressMessage show={props.isLoadingArchives} />
        {!props.disabledChat && !props.isLoadingArchives && (
          <ChatMessages
            channel={props.channel}
            groupedMessages={props.groupedMessages}
            files={props.files}
            patient={props.patient}
            roomName={props.roomName}
          />
        )}
        {props.careyTyping && !props.disabledChat && !props.isLoadingArchives && (
          <CareyTyping status={props.careyStatus} />
        )}
        {props.careyTimedOut && !props.disabledChat && !props.isLoadingArchives && (
          <div className="carey-timeout" aria-live="polite">
            {CAREY_DISPLAY_NAME} didn’t respond. Please send your message again to try.
          </div>
        )}
        {props.replySuggestions}
        {props.disabledChat && <ChatDisabled {...props} />}
        <div
          style={{ float: 'left', clear: 'both' }}
          ref={(el) => {
            props.setMessagesEnd(el);
          }}
        />
      </>
    </div>
    {/* Footer slot: the input bar when connected, otherwise the "Not Connected" banner takes its
        place at the bottom of the page (rather than scrolling inside the message list). */}
    {!props.disabledChat &&
      (props.connected ? <InputBar {...props} /> : <ChatDisconnected {...props} />)}
    <div className="saf-android-quickfix" />
  </div>
);

// Shown while Carey is composing a reply (the patient's message is the latest in the room).
// When a tool is running, `status` carries a specific label ("Looking up your appointments…").
const CareyTyping = ({ status }) => (
  <div className="carey-typing" aria-live="polite">
    <img className="carey-typing__icon" src={careyIcon} alt="" />
    <div className="carey-typing__bubble">
      <span className="carey-typing__label">{status || `${CAREY_DISPLAY_NAME} is thinking`}</span>
      <span className="carey-typing__dots" aria-hidden>
        <span />
        <span />
        <span />
      </span>
    </div>
  </div>
);

const ChatDisconnected = (props) => (
  <div className="chat-action chat-action--footer">
    <span>Not Connected to Care& Chat Service</span>
    {props.onReconnect && (
      <DefaultButton
        name="Reconnect"
        className="blue mt-2"
        icon="link"
        onClick={props.onReconnect}
        loading={props.connecting}
      />
    )}
  </div>
);

const ChatDisabled = (props) => (
  <div className="chat-action">
    <span>No Chat available</span>
    <DefaultButton
      name="Start Chat"
      icon="comment"
      className="blue mt-2"
      onClick={props.startChat}
      loading={props.creatingChat}
    />
  </div>
);

const ChatMessages = ({ channel, groupedMessages, roomName, patient }) => {
  let chatMessages = [];
  for (let index in groupedMessages) {
    let message = groupedMessages[index];
    chatMessages.push(
      <ChatMessage
        key={index + message.time}
        date={message.time}
        author={message.author}
        name={message.name}
        messages={message.messages || []}
        own={message.own}
        lasRead={message.lastRead}
        channel={channel}
        roomName={roomName}
        patient={patient}
      />,
    );
  }
  return chatMessages;
};

const InputBar = (props) => {
  const inputFile = useRef(null);

  function onKeyDown(event) {
    props.onKeyDown(event, props.currentMessage);
  }

  function onClick() {
    props.iconClicked();
  }

  function handleFileInput(event) {
    const files = Array.from(event.target.files);
    props.uploadFiles(files);
  }

  return (
    <div className="input-bar" onKeyDown={onKeyDown} tabIndex={0}>
      <ContentEditable
        key={props.disabledInput}
        name="send_message-bar"
        placeholder={props.disabledInput ? 'Please wait...' : 'Enter a message'}
        disabled={true}
        onChange={props.disabledInput ? undefined : props.handleTypedMessageChange}
        text={props.currentMessage}
      />
      <div className="icons">
        {props.extraIcons}
        {props.onAssistantReply && (
          <Icon
            alt={props.assistantLoading ? 'Interrupt Assistant' : 'Assistant Reply'}
            showTooltip={true}
            width="20px"
            id="assistant-button"
            src={props.assistantLoading ? 'circle-stop' : 'sparkles'}
            className="icon-assistant"
            onClick={props.onAssistantReply}
          />
        )}
        {!!props.uploadFiles && (
          <>
            <Icon
              alt="Send File"
              showTooltip={true}
              loading={props.uploadingFiles}
              width="20px"
              id="send_file-button"
              src="paperclip"
              className="icon-attachments"
              onClick={() => {
                inputFile.current.click();
              }}
            />
            <input
              type="file"
              id="file"
              ref={inputFile}
              accept="image/jpeg, image/png, application/pdf, image/heic"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </>
        )}
        {!props.disabledInput && (
          <Icon
            alt="Send Chat Message"
            showTooltip={true}
            width="20px"
            id="send_message-button"
            src="message"
            className="icon-chat"
            onClick={onClick}
          />
        )}
        {!props.disableSMS && props.patient && !props.disabledInput && (
          <SendSMSIcon
            message={props.currentMessage}
            patient={props.patient}
            onSuccess={props.onSentSMS}
          />
        )}
      </div>
    </div>
  );
};

const TopBar = (props) => {
  if (props.renderTopBar !== undefined) return props.renderTopBar();
  return null;
};

export default ChatWindowView;
